import {
  ReactNode,
  FC,
  useEffect,
  useRef,
  forwardRef,
  useCallback,
  type ComponentProps,
} from 'react'

import { Navigate, useLocation, useNavigate, useParams } from 'react-router'

import { useDomain } from '@tribeplatform/react-components/common/hooks'
import { Link } from '@tribeplatform/react-components/Link'
import { TribeRouter, type TribeRouterType } from '@tribeplatform/react-sdk'

import { AppInitialProps } from '../@types/index.js'
import { RuntimeConfigs } from '../configs/runtime.js'
import { Otsm } from './components/Otsm.js'
import { useAnalytics } from './hooks/useAnalytics.js'
import { useFullStory } from './hooks/useFullStory.js'
import { useWindowTribe } from './hooks/useWindowTribe.js'
import {
  TribeEmitter,
  TribeEmitterEvent,
} from './lib/tribe-events/TribeEmitter.js'

type TribeWebProps = {
  pageProps: AppInitialProps
  children: ReactNode
}

export const TribeWeb: FC<TribeWebProps> = ({ children, pageProps }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const previousPage = useRef<string>(location.pathname)

  useWindowTribe()
  useFullStory()
  const { domain, subfolder } = useDomain()

  const analytics = useAnalytics()

  useEffect(() => {
    analytics.trackPageView(location.pathname)
  }, [location.pathname, analytics])

  // emit an event on route change
  useEffect(() => {
    const page = location.pathname
    TribeEmitter.emit(TribeEmitterEvent.PAGE_VIEW, {
      page,
      previousPage:
        previousPage.current !== page ? previousPage.current : undefined,
    })

    previousPage.current = page
  }, [location.pathname])

  // clear listeners on component unmount
  useEffect(
    () => () => {
      TribeEmitter.clearListeners(TribeEmitterEvent.PAGE_VIEW)
    },
    [],
  )

  useEffect(() => {
    if (
      subfolder &&
      !RuntimeConfigs.FORCED_NETWORK_DOMAIN &&
      `${window?.location?.host?.replace(/^www\./, '')}${
        window?.location?.pathname
      }`.indexOf(domain?.replace(/^www\./, '')) !== 0
    ) {
      let path = window?.location?.pathname || ''
      if (path.indexOf(subfolder) === 0) {
        path = path.replace(subfolder, '')
      }
      window.location.href = `https://${domain}${subfolder}${path}`
      logger.warn('Redirecting original domain to masked domain with subfolder')
    }
  }, [])

  return (
    <TribeRouter
      {...{
        location,
        params,
        Link: forwardRef<
          HTMLAnchorElement,
          ComponentProps<TribeRouterType['Link']>
        >(({ children, ...props }, ref) => (
          <Link ref={ref} {...props}>
            {children}
          </Link>
        )),
        Redirect: props => <Navigate to={props.to} {...props} />,
        redirect: useCallback(
          href => {
            const isInternal =
              href && href?.indexOf('/') === 0 && href?.indexOf('//') !== 0

            if (isInternal) {
              window.location.href = `${subfolder}${href}`
            } else {
              window.location.href = href
            }
          },
          [subfolder],
        ),
        push: useCallback(
          path => {
            navigate(path)
          },
          [navigate],
        ),
        replace: useCallback(
          path => {
            navigate(path, { replace: true })
          },
          [navigate],
        ),
      }}
    >
      {children}
      {pageProps.otsm && <Otsm otsm={pageProps.otsm} />}
    </TribeRouter>
  )
}
